import Box from '@mui/material/Box';
import { Chip } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';

import DataTable from 'components/common/DataTable';
import CustomContainer from 'components/CustomContainer';
import { emailState, modalState, userListState } from 'pages/Users/state';

interface User {
  id: number;
  name: string;
  isActive: boolean;
  email: string;
  roles: any;
  createdAt: string;
  lastLoginAt: string;
}

const customStyles = {
  headRow: {
    style: {
      color: 'white',
      backgroundColor: '#6699CC' //blue
    }
  },
  headCells: {
    style: {
      fontSize: '16px',
      fontWeight: 'bold',
      border: '1px solid #ddd', // add border to header cells
      justifyContent: 'center'
    }
  },
  rows: {
    style: {
      minHeight: '56px',
      '&:nth-child(even)': {
        backgroundColor: '#FFFFFF' //white
      },
      '&:nth-child(odd)': {
        backgroundColor: '#D0D5E8' //grey
      },
      border: '1px solid #ddd' // add border to rows
    }
  },
  cells: {
    style: {
      fontSize: '14px',
      paddingLeft: '8px',
      paddingRight: '8px',
      width: 'min-content',
      borderRight: '1px solid #ddd', // add right border to cells
      justifyContent: 'center'
    }
  }
};

// =============================================================================
export default function UsersTable() {
  const setEmail = useSetRecoilState(emailState);
  const setModal = useSetRecoilState(modalState);
  const users = useRecoilValueLoadable<User[]>(userListState);

  const columns: any = [
    {
      cell: (e: User) => (
        <button
          onClick={() => {
            setEmail(e.email);
            setModal(true);
          }}
        >
          Edit
        </button>
      ),
      width: '100px'
    },
    {
      name: 'ID',
      selector: (user: User) => user.id,
      sortable: true,
      width: '100px'
    },
    {
      name: 'Name',
      selector: (user: User) => user.name,
      cell: (user: User) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
        >
          <span>{user.name}</span>
          {user.isActive === false && (
            <Chip
              size='small'
              color='error'
              icon={<LockIcon />}
              label='Disabled'
            />
          )}
        </Box>
      ),
      sortable: true,
      width: '250px'
    },
    {
      name: 'Email',
      selector: (user: User) => user.email,
      sortable: true
    },
    {
      name: 'Role',
      selector: (user: User) => user.roles.join(','),
      sortable: true
    },
    {
      name: 'Created',
      selector: (user: User) => {
        const time = parseInt(user.createdAt);
        const formattedDate = new Date(time).toLocaleDateString();
        return formattedDate === 'Invalid Date' ? 'NA' : formattedDate;
      },
      sortable: true
    },
    {
      name: 'Last login',
      selector: (user: User) => {
        const time = parseInt(user.lastLoginAt);
        const formattedDate = new Date(time).toLocaleDateString();
        return formattedDate === 'Invalid Date' ? 'NA' : formattedDate;
      },
      sortable: true
    }
  ];

  return (
    <CustomContainer>
      <div style={{ height: 32 }} />
      <Box sx={{ width: '100%' }}>
        <DataTable
          title=''
          columns={[...columns]}
          data={users.state === 'hasValue' ? users.contents : []}
          progressPending={users.state === 'hasValue' ? false : true}
          pagination
          customStyles={customStyles}
          paginationRowsPerPageOptions={[20, 40, 60, 80, 100]}
        />
      </Box>
    </CustomContainer>
  );
}
